import React from 'react';
import { useDisputesQueue } from '../../../hooks/admin/useDisputesQueue';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { formatDistanceToNow } from 'date-fns';
import { ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';

export function QueueTable({ status, reason, tier, categoryId, ageHours, search, cursor, onOpenDispute, onPageChange }) {
  const { data, isLoading, error } = useDisputesQueue({
    status,
    reason,
    tier,
    categoryId,
    ageHours,
    search,
    cursor,
    limit: 50,
  });

  if (isLoading) {
    return (
      <div className="mt-4 space-y-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="animate-pulse h-16 bg-gray-100 rounded"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">
          Failed to load disputes queue. Please try refreshing the page.
        </p>
      </div>
    );
  }

  if (!data || !data.disputes || data.disputes.length === 0) {
    return (
      <div className="mt-4 p-12 text-center bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-600">No disputes match your filters.</p>
        <p className="text-sm text-gray-500 mt-1">Try adjusting your search criteria.</p>
      </div>
    );
  }

  const getStatusBadgeColor = (disputeStatus) => {
    switch (disputeStatus) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'negotiating':
        return 'bg-yellow-100 text-yellow-800';
      case 'needs_moderation':
        return 'bg-orange-100 text-orange-800';
      case 'escalated':
        return 'bg-red-100 text-red-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="mt-4">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dispute ID</TableHead>
              <TableHead>Order #</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Age</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Buyer</TableHead>
              <TableHead>Seller</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-center">Files?</TableHead>
              <TableHead>Last Activity</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.disputes.map((dispute) => {
              const createdDate = new Date(dispute.created_at);
              const updatedDate = new Date(dispute.updated_at);
              const ageHrs = ((Date.now() - createdDate.getTime()) / (1000 * 60 * 60)).toFixed(1);

              return (
                <TableRow key={dispute.id}>
                  <TableCell className="font-mono text-xs">
                    {dispute.id.slice(0, 8)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {dispute.order_id ? dispute.order_id.slice(0, 8) : '—'}
                  </TableCell>
                  <TableCell className="text-sm" title={createdDate.toISOString()}>
                    {formatDistanceToNow(createdDate, { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold">
                    {ageHrs}h
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusBadgeColor(dispute.status)}>
                      {dispute.status.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm capitalize">
                    {dispute.reason?.replace(/_/g, ' ') || '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {dispute.buyer_name || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{dispute.seller_name || 'Unknown'}</span>
                      {dispute.seller_tier !== null && dispute.seller_tier !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          T{dispute.seller_tier}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {dispute.category || '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    {dispute.has_deliverables ? (
                      <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                    ) : (
                      <XCircle className="h-4 w-4 text-gray-400 mx-auto" />
                    )}
                  </TableCell>
                  <TableCell className="text-sm" title={updatedDate.toISOString()}>
                    {formatDistanceToNow(updatedDate, { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onOpenDispute(dispute.id)}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {(data.next_cursor || data.prev_cursor) && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing {data.disputes.length} dispute{data.disputes.length !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(data.prev_cursor)}
              disabled={!data.prev_cursor}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(data.next_cursor)}
              disabled={!data.next_cursor}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
